/**
 * Diet Plan intake, medical gates, consent, loading/result copy.
 * Key prefix: "diet."
 *
 * NEVER translated: the AI-generated plan content itself (headline, meal
 * items, routine actions, favour/limit/grocery lists, support.why,
 * plan.disclaimer) — those render exactly as the model returns them.
 */

export const dietPlan = {
  en: {
    "diet.badge.free": "Free, no payment needed",
    "diet.title": "Your free diet plan & daily routine",
    "diet.subtitle":
      "Seven days of Indian meals, a routine you can actually keep, and a grocery list — built around your body, your region and your goal.",

    "diet.section.track": "What are we planning for?",
    "diet.section.contact": "Where should we send it?",
    "diet.section.about": "About you",
    "diet.section.medical": "Anything we should plan around?",

    "diet.field.fullName": "Full name",
    "diet.field.email": "Email",
    "diet.field.whatsapp": "WhatsApp number",
    "diet.field.age": "Age",
    "diet.field.height": "Height (cm)",
    "diet.field.weight": "Weight (kg)",
    "diet.field.sleep": "Hours of sleep on a typical night",
    "diet.field.medications": "Medicines or supplements you take (optional)",
    "diet.field.medicationsPlaceholder": "e.g. thyroid tablet, metformin",
    "diet.field.allergies": "Allergies or foods to avoid (optional)",
    "diet.field.allergiesPlaceholder": "e.g. lactose, peanuts",

    "diet.bmi.prefix": "BMI",

    "diet.choices.gender": "Gender",
    "diet.gender.female": "Female",
    "diet.gender.male": "Male",
    "diet.gender.other": "Prefer not to say",

    "diet.choices.dietType": "How do you eat?",
    "diet.dietType.vegetarian": "Vegetarian",
    "diet.dietType.eggetarian": "Eggetarian",
    "diet.dietType.non-vegetarian": "Non-vegetarian",
    "diet.dietType.vegan": "Vegan",
    "diet.dietType.jain": "Jain",

    "diet.choices.region": "Where in India?",
    "diet.region.north": "North India",
    "diet.region.south": "South India",
    "diet.region.east": "East India",
    "diet.region.west": "West India",
    "diet.region.northeast": "North-East",

    "diet.choices.activity": "Activity level",
    "diet.activity.sedentary": "Mostly sitting",
    "diet.activity.light": "Light activity",
    "diet.activity.moderate": "Moderate (3–4 workouts)",
    "diet.activity.active": "Very active",

    "diet.back": "Back",
    "diet.continue": "Continue",

    "diet.medical.intro":
      "Tick any pre-existing medical conditions. Some conditions need a clinician rather than a generated plan — we'll tell you honestly if that's the case.",

    "diet.condition.none": "None of these",
    "diet.condition.thyroid": "Thyroid (hypo / hyper)",
    "diet.condition.diabetes_t2": "Type 2 diabetes or prediabetes",
    "diet.condition.diabetes_t1": "Type 1 diabetes",
    "diet.condition.hypertension": "High blood pressure",
    "diet.condition.kidney": "Kidney disease",
    "diet.condition.liver": "Diagnosed liver disease",
    "diet.condition.heart": "Heart condition",
    "diet.condition.gi": "IBS / IBD / acidity",
    "diet.condition.pregnant": "Pregnant or breastfeeding",
    "diet.condition.eating_disorder": "History of an eating disorder",
    "diet.condition.cancer": "Cancer treatment (current or recent)",

    "diet.trackQuestion.pcos": "How regular are your cycles, and what bothers you most right now?",
    "diet.trackQuestion.fatty-liver":
      "How often do you drink alcohol, and what did your last scan or report say?",
    "diet.trackQuestion.wellness":
      "What's the one thing you'd most like to change — sleep, energy or stress?",

    "diet.consent.marketing": "Send my plan and occasional wellness tips on WhatsApp and email.",

    "diet.cta.generating": "Building your plan…",
    "diet.cta.submit": "Get my free plan",
    "diet.error.generic": "Something went wrong. Please try again.",

    "diet.disclaimer":
      "This plan is general nutrition education, not medical advice, diagnosis or treatment. It does not replace your doctor or a registered dietitian. Stop and seek medical advice if you feel unwell, and always tell your doctor about anything you take alongside prescribed medication.",

    "diet.quizPrompt": "Not sure which goal fits?",
    "diet.quizLink": "Take the 60-second quiz",

    "diet.track.pcos.label": "PCOS / hormonal balance",
    "diet.track.pcos.blurb":
      "Insulin-first eating for irregular cycles, acne, hair fall and weight that won't move.",
    "diet.track.fatty-liver.label": "Fatty liver (NAFLD)",
    "diet.track.fatty-liver.blurb":
      "Lower the fructose and refined-carb load, raise protein, fibre and choline.",
    "diet.track.wellness.label": "General wellness — sleep, stress, energy",
    "diet.track.wellness.blurb":
      "Steadier energy through the day and a nervous system that actually switches off at night.",

    "diet.bmiBand.under": "Below the healthy range",
    "diet.bmiBand.healthy": "Healthy range (Asian-Indian cut-off)",
    "diet.bmiBand.above": "Above the healthy range",
    "diet.bmiBand.overweight": "Overweight (Asian-Indian cut-off)",
    "diet.bmiBand.obese": "Obese range",

    "diet.result.consentHeadline": "Want your plan and follow-ups on WhatsApp?",
    "diet.result.another": "Build another plan",

    "diet.gate.under_18":
      "This plan is built for adults. For anyone under 18, please speak with a paediatrician or a registered dietitian.",
    "diet.gate.over_85":
      "Above 85, nutrition targets need individual medical review. Please speak with your doctor.",
    "diet.gate.bmi_low":
      "Your BMI is in a range where a self-serve plan isn't appropriate. Please see a doctor or clinical dietitian first.",
    "diet.gate.condition_pregnant":
      "Nutrition during pregnancy and breastfeeding needs to be supervised by your obstetrician or a clinical dietitian — a generated plan isn't safe here.",
    "diet.gate.condition_eating_disorder":
      "With a history of disordered eating, structured calorie plans can do more harm than good. Please work with a clinician you trust.",
    "diet.gate.condition_diabetes_t1":
      "Type 1 diabetes needs carbohydrate planning matched to your insulin, which only your treating team can do safely.",
    "diet.gate.condition_kidney":
      "Kidney disease changes protein, potassium and fluid targets significantly. Please get a plan from your nephrology team.",
    "diet.gate.condition_cancer":
      "During or shortly after cancer treatment, nutrition should be planned with your oncology team.",

    "diet.plan.printPdf": "Download / print PDF",
    "diet.plan.stat.bmi": "BMI",
    "diet.plan.stat.calories": "Daily calories",
    "diet.plan.stat.caloriesHint": "kcal guide",
    "diet.plan.stat.protein": "Protein",
    "diet.plan.stat.proteinHint": "per day",
    "diet.plan.stat.length": "Plan length",
    "diet.plan.stat.lengthHint": "repeatable",
    "diet.plan.sevenDay": "Your 7-day plan",
    "diet.plan.routine": "Your daily routine",
    "diet.plan.favour": "Lean on these",
    "diet.plan.limit": "Keep occasional",
    "diet.plan.grocery": "Weekly grocery list",
    "diet.plan.pairs": "Pairs with your plan",
    "diet.plan.seeProduct": "See {title}",
  },
} as const;
